'use client';

import { useCallback } from 'react';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { Share } from '@velocity-exchange/icons';
import { calculatePnlPctFromPosition } from '@velocity-exchange/common';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import ValueChangeBlink from 'src/components/Utils/ValueChangeBlink';
import Divider from 'src/components/primitives/Divider/Divider';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { signDependentValue } from 'src/utils/signDependentValue';
import { cj } from 'src/utils/classNames';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useModal from 'src/components/Modals/hooks/useModal';
import { getMarginUsedForPosition } from 'src/utils/position';
import { AccountOpenPosition } from 'src/@types/positions';
import { useUnclaimablePnlWarning } from 'src/hooks/useUnclaimablePnlWarning';
import { UnclaimablePnlWarning } from '../UnclaimablePnlWarning';

interface PnLCellProps {
	position: AccountOpenPosition;
}

export function PnLCell({ position }: PnLCellProps) {
	const showAccountValues = useShowAccountValues();
	const { show } = useModal();
	const [pnlReference] = useSyncLocalStorage<'mark' | 'oracle'>(
		'pnlReference',
		'mark'
	);

	const positionPnl = BigNum.from(
		pnlReference === 'mark' ? position.pnlVsMark : position.pnlVsOracle,
		QUOTE_PRECISION_EXP
	);
	const fundingPnl = BigNum.from(
		position.feesAndFundingPnl,
		QUOTE_PRECISION_EXP
	);

	const pnl = positionPnl.toNum();
	const notionalPnl = showAccountValues
		? `${positionPnl.toNotional()}`
		: '∗∗∗∗∗∗';

	const driftClient = useDriftStore((s) => s.driftClient.client);
	const user = driftClient?.hasUser() ? driftClient?.getUser() : undefined;

	const percentageNum = calculatePnlPctFromPosition(
		positionPnl.val,
		position,
		user
			? getMarginUsedForPosition(user, position.marketIndex, false)
			: undefined
	);

	const percentagePnl = `${percentageNum.toFixed(2)}%`;

	const unclaimablePnlWarning = useUnclaimablePnlWarning(position);

	const handleShowPnlCard = useCallback(() => {
		show({
			type: 'pnl_card',
			props: {
				position,
				actualExitPrice: BigNum.from(
					position.exitPrice,
					PRICE_PRECISION_EXP
				).toNum(),
				pnl,
				triggeredFrom: 'open_positions_table',
				isIsolated: position.isIsolated,
			},
		});
	}, [show, position, pnl]);

	if (position.isIsolatedBalanceOnly) {
		return <span className="text-text-secondary">-</span>;
	}

	return (
		<span
			className={cj(
				'relative flex w-full items-center gap-x-xs',
				signDependentValue(pnl, {
					positive: 'text-text-positive-green-button',
					negative: 'text-text-negative-red-button',
					zero: 'text-text-default',
				})
			)}
		>
			<Tooltip
				decoration="cursor-help"
				contentClassName="flex flex-col gap-y-sm"
				content={
					<>
						<div>
							Position P&L based on current{' '}
							{pnlReference === 'mark' ? 'mark' : 'oracle'} price, excluding
							fees and funding. P&L upon closing your position may differ due to
							exit price.
						</div>
						<Divider className="bg-text-tertiary" />
						<div className="flex w-full flex-col gap-y-2xs">
							<div className="flex w-full items-center justify-between">
								<span>Position P&L: </span>
								<span>
									{showAccountValues ? positionPnl.toNotional() : '$ ∗∗∗∗∗∗'}
								</span>
							</div>
							<div className="flex w-full items-center justify-between">
								<span>Fees and Funding: </span>
								<span>
									{showAccountValues ? fundingPnl.toNotional() : '$ ∗∗∗∗∗∗'}
								</span>
							</div>
						</div>
					</>
				}
			>
				<ValueChangeBlink
					className="flex flex-col gap-y-xs"
					value={notionalPnl}
				>
					<span>{notionalPnl}</span>
					{!position.isDustPosition && (
						<span className="typo-body-xs">{percentagePnl}</span>
					)}
				</ValueChangeBlink>
			</Tooltip>

			{unclaimablePnlWarning && (
				<UnclaimablePnlWarning warningMessage={unclaimablePnlWarning} />
			)}

			<IconButton
				size="md"
				variant="tertiary"
				className="-m-sm p-sm"
				onClick={(e) => {
					e.stopPropagation();
					handleShowPnlCard();
				}}
				icon={Share}
			/>
		</span>
	);
}
